import { AccredibleButtonComponentModule } from '@accredible-frontend-v2/new-components/button';
import { AccredibleIconComponentModule } from '@accredible-frontend-v2/new-components/icon';
import { AccredibleLinkComponentModule } from '@accredible-frontend-v2/new-components/link';
import { AccredibleLanguageServiceModule } from '@accredible-frontend-v2/services/language';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { ExpansionPanelGroupService } from './expansion-panel-group.service';
import { expansionPanelAnimation, ExpansionPanelState } from './expansion-panel.animation';

// Counter for generating unique element ids
let expansionPanelUniqueId = 0;

export interface ExpansionPanelTriggerConfig {
  style?: 'button' | 'link' | 'custom';
  width?: 'auto' | 'full';
  hidden?: boolean;
  expandText?: string;
  expandAriaLabel?: string;
  collapseText?: string;
  collapseAriaLabel?: string;
  dataCy?: string;

  /**
   * Optional grouping key. Only panels with the same expandGroup will
   * mutually collapse/expand each other.
   * See ExpansionPanelGroupService for more details.
   */
  expandGroup?: string;
}

const DEFAULT_TRIGGER_CONFIG: ExpansionPanelTriggerConfig = {
  style: 'link',
  width: 'auto',
};

@Component({
  standalone: true,
  selector: 'accredible-expansion-panel',
  imports: [
    AccredibleIconComponentModule,
    AccredibleButtonComponentModule,
    AccredibleLinkComponentModule,
    AccredibleLanguageServiceModule,
  ],
  templateUrl: './expansion-panel.component.html',
  styleUrls: ['./expansion-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [expansionPanelAnimation],
})
export class AccredibleExpansionPanelComponent {
  private readonly _group = inject(ExpansionPanelGroupService);
  private readonly _destroyRef = inject(DestroyRef);

  expanded = model<boolean>(false);
  disabled = input<boolean>(false);
  triggerConfig = input<ExpansionPanelTriggerConfig>({});

  trigger = output<boolean>();

  triggerBtnEl = viewChild<ElementRef<HTMLButtonElement>>('triggerBtn');
  expandableRegionEl = viewChild<ElementRef<HTMLElement>>('expandableRegionEl');

  expandableRegionAriaLabel = signal<string>('');

  readonly dataCy = computed(() => this.triggerConfig().dataCy);

  readonly triggerBtnId = `triggerBtn${expansionPanelUniqueId}`;
  readonly panelContentId = `panelContent${expansionPanelUniqueId++}`;
  readonly expansionPanelState = ExpansionPanelState;

  private readonly _triggerConfig = computed(() => this._buildTriggerConfig());

  // computed group id (may be undefined). If undefined -> this panel is NOT part of any
  // mutual-exclusion group and will not affect other panels.
  private readonly _groupId = computed(() => this._triggerConfig().expandGroup ?? null);

  constructor() {
    effect(() => {
      if (!this.expanded()) {
        this.triggerBtnEl()?.nativeElement?.focus();
      }
    });

    effect(() => {
      // Only subscribe to group changes when this panel belongs to a named expandGroup.
      const groupId = this._groupId();

      if (groupId) {
        this._group
          .active$(groupId)
          .pipe(
            filter((activeId) => activeId !== this.panelContentId && this.expanded()),
            takeUntilDestroyed(this._destroyRef),
          )
          .subscribe(() => {
            this.expanded.set(false);
          });
      }
    });
  }

  onExpand(): void {
    if (this.expanded()) {
      this._focusInput();
    }
  }

  onTrigger(): void {
    if (this.disabled()) {
      this.trigger.emit(false);
      return;
    }

    const willExpand = !this.expanded();
    this.expanded.set(willExpand);
    this.trigger.emit(this.expanded());

    // notify group service so other panels collapse
    const gid = this._groupId();

    if (!gid) {
      return;
    }

    if (willExpand) {
      this._group.setActive(gid, this.panelContentId);
    } else {
      // collapsing this panel -> clear active for this group
      this._group.setActive(gid, null);
    }
  }

  private _focusInput(): void {
    // Focus the first input or textarea element inside the panel if there is one
    const inputOrTextarea =
      this.expandableRegionEl()?.nativeElement.querySelector('input, textarea');
    if (inputOrTextarea) {
      (inputOrTextarea as HTMLElement)?.focus();
    }
  }

  private _buildTriggerConfig(): ExpansionPanelTriggerConfig {
    const userConfig = this.triggerConfig();

    // Merge with defaults
    const mergedConfig = {
      ...DEFAULT_TRIGGER_CONFIG,
      ...userConfig,
    };

    return {
      ...mergedConfig,
    };
  }
}
